import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, RouterStateSnapshot } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { UserClient } from '../../../api/app-types/client.app';
import { User } from '../../../api/app-types/user.app';
import { USER_SERVICE } from '../../../api/services/user.service';
import { CurrentUserService } from '../../../core/services/current-user.service';
import { PaginatedResponse } from '../../pagination/paginated-response';
import { LocalStorageService } from '../../storage/local-storage.service';

const resolveInitialPageSize = (
	storage: LocalStorageService,
	auth: MsalService | null,
	preferenceKey: string,
	defaultPageSize: number,
	pageSizeOptions: number[],
) => {
	const userId = storage.resolveCurrentUserId(auth);
	if (!userId) return defaultPageSize;

	const saved = storage.getPageSize(userId, preferenceKey);
	return saved && pageSizeOptions.includes(saved) ? saved : defaultPageSize;
};

export const userResolver: ResolveFn<User> = async (_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
	const currentUserService = inject(CurrentUserService);
	const response = await currentUserService.getCurrentUser();
	return response;
};

export const userByIdResolver: ResolveFn<User> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(USER_SERVICE);
	const id = _route.paramMap.get('id');
	const response = await api.getSingle(id!);
	return response;
};

export const usersResolver: ResolveFn<PaginatedResponse<User>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(USER_SERVICE);
	const env = inject(ENVIRONMENT);
	const storage = inject(LocalStorageService);
	const auth = inject(MsalService, { optional: true });
	const initialRange = resolveInitialPageSize(storage, auth, 'user-list', env.defaultPageSize, env.pageSizeOptions);
	const response = await api.getAll({
		offset: 0,
		range: initialRange,
		activeUsers: true,
		sortBy: 'lastName',
		ascending: true,
	});
	return response;
};

export const userClientsResolver: ResolveFn<PaginatedResponse<UserClient>> = async (
	_route: ActivatedRouteSnapshot,
	_state: RouterStateSnapshot,
) => {
	const api = inject(USER_SERVICE);
	const env = inject(ENVIRONMENT);
	const storage = inject(LocalStorageService);
	const auth = inject(MsalService, { optional: true });
	const initialRange = resolveInitialPageSize(
		storage,
		auth,
		'user-assignment-transfer',
		env.defaultPageSize,
		env.pageSizeOptions,
	);
	const response = await api.getUserClients({ offset: 0, range: initialRange });
	return response;
};
